import { z } from "zod";
import type { Operation } from "../types";

/**
 * RO-2-02 — `resolveConflict` (`excellent/src/sync/actions.ts`), given a
 * contracted HTTP twin. Backs the `/database/conflicts` page's fleet/device
 * CRR merge-conflict queue (`sync_conflicts`, via `@excellent/sync`'s
 * `resolveSyncConflict`) — a DIFFERENT table and feature from
 * `integration-conflicts.ts`'s `integration_conflicts` (a third-party
 * integration sync's field-merge conflicts). The two are easy to conflate by
 * name; docs/rollover/inventory/actions.md groups this action's file under
 * "Domain: settings/integrations" (line 555 of the inventory, "integrations,
 * sync, OAuth, connections") alongside the OAuth/connection actions, which is
 * why it is converted here rather than by a database-page-focused task —
 * kept in its own file/path (`/database/conflicts/*`, not
 * `/settings/integrations/*`) so the URL matches the page it actually backs.
 */

const resolveChoice = z.union([
  z.literal("local"),
  z.literal("incoming"),
  z.object({ value: z.string() }),
]);

const resolveBody = z.object({
  conflictUid: z.string().min(1),
  choose: resolveChoice,
});
type ResolveBody = z.infer<typeof resolveBody>;
const resolveResponse = z.object({ ok: z.boolean(), reason: z.string().optional() });
export type SyncConflictResolveResponse = z.infer<typeof resolveResponse>;

// ── RO-3-11 (#44304) — the read that page needs ─────────────────────────────
//
// RO-2-02 converted the page's MUTATION and stopped there, because the page's
// data came from a server component calling `listOpenSyncConflicts` /
// `countResolvedConflicts` (`excellent/src/sync/db.ts`) directly — there was no
// action to convert. Porting the page needs the read too.
//
// The `list` MOUNT lives on the `database` namespace (`server/routes/database.ts`)
// while `resolve` is mounted from `routes/integrations.ts`. That looks like a
// split and is a deliberate one: RO-2-02 grouped the action by the inventory's
// DOMAIN (integrations/sync) and this contract's own header explains why. Both
// serve `/api/database/conflicts/*`, which is what a reader greps for, and
// putting a new read into another item's in-flight file to chase tidiness is a
// worse trade than a stated split.

/** One open conflict awaiting reconciliation (mirrors `@excellent/sync`'s `OpenConflict`). */
export const openConflictSchema = z.object({
  uid: z.string(),
  entity_table: z.string(),
  row_uid: z.string(),
  column_name: z.string(),
  base_value: z.string().nullable(),
  local_value: z.string().nullable(),
  incoming_value: z.string().nullable(),
  detected_at: z.string(),
});
export type OpenConflictDto = z.infer<typeof openConflictSchema>;

const listResponse = z.object({
  conflicts: z.array(openConflictSchema),
  /** Count of already-reconciled conflicts, for the page's toolbar tally. */
  resolved: z.number(),
});
export type SyncConflictListResponse = z.infer<typeof listResponse>;

/**
 * READ — the open `sync_conflicts` queue for the ACTIVE workspace, plus the
 * resolved tally.
 *
 * Workspace scoping is the load-bearing part and it is preserved verbatim from
 * the page it replaces: a device only reconciles its own workspaces'
 * divergences, and a membership-less caller (`workspaceId === null`) gets an
 * EMPTY queue — never `undefined` passed through to the query, which would widen
 * it to the all-workspaces operator view.
 */
const listOp: Operation<void, void, void, SyncConflictListResponse> = {
  method: "GET",
  path: () => "/api/database/conflicts",
  response: listResponse,
};

// Resolve is declared AFTER list so `invalidates` can name the read by value
// (RO-5-02b / #44476). Original: `revalidatePath(CONFLICTS_PATH)` →
// `/database/conflicts`, which this list now serves.
const resolveOp: Operation<void, void, ResolveBody, SyncConflictResolveResponse> = {
  method: "POST",
  path: () => "/api/database/conflicts/resolve",
  body: resolveBody,
  response: resolveResponse,
  // Port of `revalidatePath("/database/conflicts")` — resolving a conflict
  // removes it from the open queue and bumps the resolved tally that `list`
  // returns. Bare reference: list has no path params.
  invalidates: [listOp],
};

export const databaseConflicts = {
  resolve: resolveOp,
  list: listOp,
};
